// Customer-facing single recipient view.
// Header card + request history table + Edit / Delete actions.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Pencil, Trash2, Phone, MapPin, Heart, Plus, Loader2,
  Languages, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill, EmptyState } from '../../components/shared/primitives';
import { CUSTOMER_NAV } from '../_navs';
import { recipients, requests as requestsApi, type ServiceRequestWithItems } from '../../lib/api';
import { formatHtgMinor, formatDisplayFromHtgMinor } from '../../lib/money';
import { Button } from '../../components/stripe-ui/Button';
import { Modal } from '../../components/shared/Modal';

const ACTIVE_STATUSES = new Set([
  'submitted', 'quote_pending', 'quoted', 'customer_approval_pending',
  'payment_pending', 'paid', 'assigned_to_merchant', 'in_progress',
  'ready_for_delivery', 'delivered',
]);

export function CustomerRecipientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const recipId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const recipQ = useQuery({
    queryKey: ['me', 'recipient', recipId],
    queryFn:  () => recipients.get(recipId),
    enabled:  !isNaN(recipId),
  });
  // Pull all my requests and filter — cheaper than a per-recipient endpoint
  // for now and reuses already-cached data.
  const requestsQ = useQuery({ queryKey: ['me', 'requests'], queryFn: () => requestsApi.list() });

  const recip = recipQ.data;
  const reqs  = (requestsQ.data ?? []).filter((r) => r.recipient_id === recipId);
  const activeCount    = reqs.filter((r) => ACTIVE_STATUSES.has(r.status)).length;
  const confirmedCount = reqs.filter((r) => r.status === 'confirmed').length;
  const totalSpentHtg  = reqs.filter((r) => r.status === 'confirmed').reduce((a, r) => a + (r.cart_subtotal_htg_minor ?? 0), 0);

  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const removeMut = useMutation({
    mutationFn: () => recipients.remove(recipId),
    onSuccess: () => {
      toast.success('Recipient removed');
      qc.invalidateQueries({ queryKey: ['me', 'recipients'] });
      navigate('/customer/recipients', { replace: true });
    },
    onError: (e: Error) => toast.error(e.message || 'Could not delete'),
  });

  if (isNaN(recipId)) return (
    <PortalShell nav={CUSTOMER_NAV} activeId="recipients" title="Recipient not found">
      <Card>Bad recipient id.</Card>
    </PortalShell>
  );

  return (
    <PortalShell
      nav={CUSTOMER_NAV}
      activeId="recipients"
      title={recip?.full_name ?? 'Recipient'}
      subtitle={recip ? `${recip.city?.name ?? ''}${recip.relationship ? ' · ' + recip.relationship : ''}` : undefined}
      actions={
        recip ? (
          <div className="flex items-center gap-2">
            <Link to="/customer/recipients" className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 text-[12px] hover:bg-slate-50">
              <Pencil className="w-3.5 h-3.5" /> Edit on list
            </Link>
            <Link to="/customer/requests/new" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12px] font-semibold hover:bg-brand-700">
              <Plus className="w-3.5 h-3.5" /> Buy for them
            </Link>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-5">
        <Link to="/customer/recipients" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-3.5 h-3.5" /> All recipients
        </Link>

        {recipQ.isLoading && (
          <Card><div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>
        )}

        {recipQ.error && (
          <Card><p className="text-[13px] text-rose-700">Couldn't load this recipient.</p></Card>
        )}

        {recip && (
          <>
            {/* Header card */}
            <Card>
              <div className="flex items-start gap-4 flex-wrap">
                <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-rose-50 text-rose-700 text-[18px] font-semibold shrink-0">
                  {initialsOf(recip.full_name)}
                </span>
                <div className="flex-1 min-w-0">
                  <h2 className="text-[22px] font-semibold tracking-tight text-slate-900 truncate">{recip.full_name}</h2>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[12.5px] text-slate-600">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {recip.city?.name ?? '—'}</span>
                    <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" /> {recip.phone_e164}</span>
                    {recip.relationship && <span className="inline-flex items-center gap-1 capitalize"><Heart className="w-3.5 h-3.5 text-rose-400" /> {recip.relationship}</span>}
                    <span className="inline-flex items-center gap-1"><Languages className="w-3.5 h-3.5 text-slate-400" /> {recip.preferred_lang.toUpperCase()}</span>
                  </div>
                  {recip.address_line && <p className="mt-2 text-[12px] text-slate-500">{recip.address_line}{recip.landmark ? ` · ${recip.landmark}` : ''}</p>}
                  {recip.notes && <p className="mt-2 text-[12px] text-slate-500 italic">"{recip.notes}"</p>}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                <Stat label="Active requests"  value={String(activeCount)}    hint="In flight" />
                <Stat label="Delivered"        value={String(confirmedCount)} hint="Lifetime" />
                <Stat label="Confirmed spend"  value={totalSpentHtg ? formatHtgMinor(totalSpentHtg) : '—'} hint={totalSpentHtg ? `≈ ${formatDisplayFromHtgMinor(totalSpentHtg)}` : 'No confirmed orders yet'} />
              </div>
            </Card>

            {/* Requests history */}
            <Card padding="p-0">
              <div className="p-5 border-b border-slate-100">
                <SectionHeader
                  title="Request history"
                  subtitle={`${reqs.length} total · ${activeCount} active`}
                  actions={
                    <Link to="/customer/requests/new" className="text-[12px] font-semibold text-brand-600 hover:underline">
                      New request →
                    </Link>
                  }
                />
              </div>
              {requestsQ.isLoading ? (
                <div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading requests…</div>
              ) : reqs.length === 0 ? (
                <EmptyState
                  icon={Heart}
                  title="Nothing sent yet"
                  body={`Start by building a cart for ${recip.full_name.split(' ')[0]}.`}
                  action={<Link to="/customer/requests/new" className="text-[13px] font-semibold text-brand-600 hover:underline">Send first request →</Link>}
                />
              ) : (
                <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
                  <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                    <tr>
                      <th className="text-left py-2.5 px-5 font-medium">Request</th>
                      <th className="text-left py-2.5 px-3 font-medium">Status</th>
                      <th className="text-left py-2.5 px-3 font-medium">Items</th>
                      <th className="text-right py-2.5 px-3 font-medium">HTG</th>
                      <th className="text-right py-2.5 px-5 font-medium">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reqs.map((r) => <RequestHistoryRow key={r.id} r={r} />)}
                  </tbody>
                </table>
              )}
            </Card>

            {/* Danger zone */}
            <Card className="border-rose-100 bg-rose-50/30">
              <SectionHeader
                title="Danger zone"
                subtitle={activeCount > 0 ? `Cancel the ${activeCount} active request${activeCount === 1 ? '' : 's'} first before removing this recipient.` : 'Removes this recipient. Past requests stay in your history.'}
              />
              <Button
                tone="danger"
                onClick={() => setConfirmingDelete(true)}
                disabled={activeCount > 0}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove recipient
              </Button>
              {activeCount > 0 && (
                <p className="mt-2 text-[12px] text-rose-700 inline-flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Server-side guard blocks delete while requests are active.
                </p>
              )}
            </Card>
          </>
        )}
      </div>

      {confirmingDelete && recip && (
        <Modal
          open
          onClose={() => setConfirmingDelete(false)}
          title={`Remove ${recip.full_name}?`}
          width="sm"
          footer={
            <>
              <Button tone="secondary" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
              <Button tone="danger" onClick={() => removeMut.mutate()} loading={removeMut.isPending} disabled={removeMut.isPending}>
                Remove
              </Button>
            </>
          }
        >
          <p className="text-[13.5px] text-slate-700">
            This removes <strong>{recip.full_name}</strong> from your recipient list. Past requests stay in your history. They cannot submit new requests on their own afterwards.
          </p>
        </Modal>
      )}
    </PortalShell>
  );
}

function initialsOf(n: string) {
  return n.split(' ').filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-slate-100 rounded-lg p-3">
      <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</p>
      <p className="mt-1 text-[18px] font-semibold tracking-tight text-slate-900">{value}</p>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function RequestHistoryRow({ r }: { r: ServiceRequestWithItems }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-3 px-5">
        <Link to={`/customer/requests/${r.id}`} className="font-mono text-cyan-700 hover:underline">REQ-{String(r.id).padStart(4, '0')}</Link>
        <p className="text-[12px] text-slate-600 mt-0.5">{r.category?.label ?? '—'}</p>
      </td>
      <td className="py-3 px-3"><StatusPill status={r.status} /></td>
      <td className="py-3 px-3 text-slate-700 text-[12px]">{r.cart_item_count > 0 ? `${r.cart_item_count} items` : '—'}</td>
      <td className="py-3 px-3 text-right font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : <span className="text-slate-400">—</span>}</td>
      <td className="py-3 px-5 text-right text-[11.5px] text-slate-500">{new Date(r.updated_at).toLocaleDateString()}</td>
    </tr>
  );
}
